import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { Box, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	NUMBER_CELL_CLASS,
	NUMBER_CELL_ROOMY_CLASS,
	ONE_LINE_CELL_CLASS,
} from "@/controls/ag-grid/OneLineCells";
import type { ProductSize } from "../models/ProductSize";
import { activeColumnDef } from "@/controls/ag-grid/components/ActiveColumn";

const numberCell = [
	ONE_LINE_CELL_CLASS,
	NUMBER_CELL_CLASS,
	NUMBER_CELL_ROOMY_CLASS,
];

interface UseTableDataProps {
	onDelete: (product: ProductSize) => void;
}

const formatTwoDecimals = (params: { value: number | null }) =>
	params.value != null ? params.value.toFixed(2) : "";

export const useTableData = ({ onDelete }: UseTableDataProps) => {
	const columnDefs: ColDef<ProductSize>[] = [
		{
			field: "Name",
			headerName: "Name",
			flex: 2,
			minWidth: 100,
			sortable: true,
			filter: true,
			tooltipField: "Name",
			cellClass: ONE_LINE_CELL_CLASS,
		},
		{
			field: "ProductTypeName",
			headerName: "Product Type",
			flex: 1.2,
			minWidth: 80,
			sortable: true,
			filter: true,
			tooltipField: "ProductTypeName",
			cellClass: ONE_LINE_CELL_CLASS,
		},
		{
			field: "SectionCode",
			headerName: "Section Code",
			flex: 0.7,
			minWidth: 56,
			sortable: true,
			cellClass: ONE_LINE_CELL_CLASS,
		},
		{
			field: "FinishSpeedGoal",
			headerName: "Fin Speed",
			flex: 0.7,
			minWidth: 56,
			cellClass: numberCell,
			valueFormatter: formatTwoDecimals,
		},
		{
			field: "Stand1GapGoal",
			headerName: "Gap St 1",
			flex: 0.7,
			minWidth: 56,
			cellClass: numberCell,
			valueFormatter: formatTwoDecimals,
		},
		{
			field: "Stand17GapGoal",
			headerName: "Gap St 17",
			flex: 0.7,
			minWidth: 56,
			cellClass: numberCell,
			valueFormatter: formatTwoDecimals,
		},
		{
			field: "TPHGoal",
			headerName: "TPH Goal",
			flex: 0.7,
			minWidth: 56,
			cellClass: numberCell,
			valueFormatter: formatTwoDecimals,
		},
		{
			field: "Weight",
			headerName: "Weight/Ft",
			flex: 0.7,
			minWidth: 56,
			cellClass: numberCell,
			valueFormatter: formatTwoDecimals,
		},
		{
			field: "RevisionTimestamp",
			headerName: "Revision Date",
			flex: 0.9,
			minWidth: 76,
			cellClass: ONE_LINE_CELL_CLASS,
			valueGetter: (params) =>
				dxtToLocalServerTime(
					`${params.data?.RevisionTimestamp}`,
					"MM/dd/yyyy",
				) || "",
		},
		{
			field: "RevisionUserName",
			headerName: "Revision User",
			flex: 0.8,
			minWidth: 70,
			sortable: true,
			cellClass: ONE_LINE_CELL_CLASS,
		},
		activeColumnDef<ProductSize>(),
		{
			headerName: "",
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			sortable: false,
			filter: false,
			resizable: false,
			suppressSizeToFit: true,
			cellRenderer: (params: ICellRendererParams<ProductSize>) => {
				if (!params.data) return null;
				return (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<Tooltip title="Delete">
							<IconButton
								size="small"
								sx={{ p: 0.25 }}
								onClick={(e) => {
									e.stopPropagation();
									onDelete(params.data as ProductSize);
								}}
								color="error"
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				);
			},
		},
	];

	return { columnDefs };
};
